import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Download, Send, Mail, Users, Calendar, CheckCircle, AlertCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewsletterManager = () => {
  const { getAuthHeader } = useAuth();
  const { toast } = useToast();
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailData, setEmailData] = useState({
    subject: '',
    content: ''
  });

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const fetchSubscribers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/newsletter/subscribers`, {
        headers: getAuthHeader()
      });
      setSubscribers(response.data.subscribers || []);
    } catch (error) {
      console.error('Failed to fetch subscribers:', error);
      toast({
        title: "Error",
        description: "Failed to load subscribers",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const exportSubscribers = async () => {
    try {
      const response = await axios.get(`${API}/admin/newsletter/export`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'newsletter_subscribers.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({ title: "Success", description: "Subscribers exported successfully" });
    } catch (error) {
      console.error('Failed to export:', error);
      toast({
        title: "Error",
        description: "Failed to export subscribers",
        variant: "destructive"
      });
    }
  };

  const sendNewsletter = async (e) => {
    e.preventDefault();
    
    if (!emailData.subject || !emailData.content) {
      toast({
        title: "Error",
        description: "Please fill in both subject and content",
        variant: "destructive"
      });
      return;
    }

    setSendingEmail(true);
    try {
      const response = await axios.post(
        `${API}/admin/newsletter/send`,
        emailData,
        { headers: getAuthHeader() }
      );
      
      toast({ 
        title: "Success", 
        description: response.data.message
      });
      
      setEmailData({ subject: '', content: '' });
    } catch (error) {
      console.error('Failed to send:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to send newsletter",
        variant: "destructive"
      });
    } finally {
      setSendingEmail(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-state">Loading...</div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1 className="heading-1">Newsletter Manager</h1>
          <p className="body-large">Manage subscribers and send newsletters</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid" style={{ marginBottom: '32px' }}>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #7c3aed, #5b21b6)' }}>
            <Users size={24} />
          </div>
          <div className="stat-content">
            <p className="stat-value">{subscribers.length}</p>
            <p className="stat-label">Total Subscribers</p>
          </div>
        </div>
      </div>

      {/* Send Newsletter */}
      <div className="settings-card" style={{ marginBottom: '32px' }}>
        <h2 className="heading-2">Send Newsletter</h2>
        <p className="body-medium" style={{ marginBottom: '1.5rem', color: 'var(--text-secondary)' }}>
          Compose and send an email to all active subscribers
        </p>
        
        <form onSubmit={sendNewsletter} className="settings-form">
          <div className="form-group">
            <label className="form-label">Email Subject</label>
            <input
              type="text"
              value={emailData.subject}
              onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
              className="form-input"
              placeholder="Enter email subject..."
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Email Content</label>
            <textarea
              value={emailData.content}
              onChange={(e) => setEmailData({...emailData, content: e.target.value})}
              className="form-textarea"
              rows={10}
              placeholder="Enter your newsletter content here..."
              required
            />
            <p className="body-small" style={{ marginTop: '0.5rem', color: 'var(--text-muted)' }}>
              Note: This is a simplified email system. In production, integrate with an email service provider (SendGrid, AWS SES, etc.)
            </p>
          </div>

          <button 
            type="submit" 
            className="btn-primary"
            disabled={sendingEmail}
          >
            <Send size={20} />
            <span>{sendingEmail ? 'Sending...' : 'Send to All Subscribers'}</span>
          </button>
        </form>
      </div>

      {/* Subscribers List */}
      <div className="settings-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <h2 className="heading-2">Subscribers List</h2>
            <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
              {subscribers.length} active subscriber{subscribers.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button onClick={exportSubscribers} className="btn-secondary">
            <Download size={20} />
            <span>Export CSV</span>
          </button>
        </div>

        {subscribers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
            <Mail size={48} style={{ opacity: 0.5, marginBottom: '16px' }} />
            <p className="body-medium">No subscribers yet</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Subscribed Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {subscribers.map((subscriber) => (
                  <tr key={subscriber.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Mail size={16} style={{ color: 'var(--text-muted)' }} />
                        <span>{subscriber.email}</span>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Calendar size={16} style={{ color: 'var(--text-muted)' }} />
                        <span>{formatDate(subscriber.subscribed_at)}</span>
                      </div>
                    </td>
                    <td>
                      <span className="badge-success">
                        <CheckCircle size={14} />
                        {subscriber.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsletterManager;
